import { call, put, takeLatest } from 'redux-saga/effects';
import { YieldOf } from 'types/utils';
import { Constants } from 'core';

import {
  createTypology,
  createdTypology,
  updateTypology,
  updatedTypology,
  deleteTypology,
  deletedTypology,
} from './actions';
import { fetchTypologies } from '../other-selection/actions';
import { logout } from '../api-auth/actions';
import getAPI from '../../api/iv-back';
import { sagaWithCallback } from '../utils';

type CreateTypologyActionPayload = ReturnType<typeof createTypology>;
type UpdateTypologyActionPayload = ReturnType<typeof updateTypology>;
type DeleteTypologyActionPayload = ReturnType<typeof deleteTypology>;

export function* createTypologySaga({ payload }: CreateTypologyActionPayload) {
  const { createTypologyApi } = getAPI();
  const response: YieldOf<typeof createTypologyApi> = yield call(createTypologyApi, payload);
  const { data, ok, problem, status } = response;
  if (status === 401 || status === 403) {
    yield put(logout());
  }
  if (ok) {
    yield put(fetchTypologies());
    yield put(createdTypology(data));
    return data;
  } else {
    const error =
      status === Constants.ALREADY_EXISTED_STATUS ? Constants.ALREADY_EXISTED_PROBLEM_CODE : problem || 'unknown';
    yield put(createdTypology(undefined, error));
    throw new Error('create typology failed: ' + error);
  }
}

export function* updateTypologySaga({ payload }: UpdateTypologyActionPayload) {
  const { updateTypologyApi } = getAPI();
  const response: YieldOf<typeof updateTypologyApi> = yield call(updateTypologyApi, payload);
  const { data, ok, problem, status } = response;
  if (status === 401 || status === 403) {
    yield put(logout());
  }
  if (ok) {
    yield put(fetchTypologies());
    yield put(updatedTypology(data));
    return data;
  } else {
    const error =
      status === Constants.ALREADY_EXISTED_STATUS ? Constants.ALREADY_EXISTED_PROBLEM_CODE : problem || 'unknown';
    yield put(updatedTypology(undefined, error));
    throw new Error('update typology failed: ' + error);
  }
}

export function* deleteTypologySaga({ meta: { id } }: DeleteTypologyActionPayload) {
  const { deleteTypologyApi } = getAPI();
  const response: YieldOf<typeof deleteTypologyApi> = yield call(deleteTypologyApi, id);
  const { ok, problem, status } = response;
  if (status === 401 || status === 403) {
    yield put(logout());
  }
  if (ok) {
    yield put(fetchTypologies());
    yield put(deletedTypology(true));
  } else {
    const error = problem || 'unknown';
    yield put(deletedTypology(false, error));
  }
}

export default function* TypologySaga() {
  yield takeLatest(createTypology.type, sagaWithCallback(createTypologySaga));
  yield takeLatest(updateTypology.type, sagaWithCallback(updateTypologySaga));
  yield takeLatest(deleteTypology.type, deleteTypologySaga);
}
